import React, {
  forwardRef,
  useState,
  useMemo,
  useCallback,
  ReactNode,
  InputHTMLAttributes,
  ChangeEvent,
  FocusEvent,
} from 'react'
import clsx from 'clsx'

export enum InputSizes {
  small = 'small',
  medium = 'medium',
  large = 'large',
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Size variant matching ButtonSizes */
  size?: InputSizes
  /** Label displayed above the input */
  label?: ReactNode
  /** Caption / helper text displayed below the input */
  caption?: ReactNode
  /** Error message or error state */
  error?: ReactNode | boolean
  /** Whether the field is required */
  isRequired?: boolean
  /** Disabled state alias */
  isDisabled?: boolean
  /** Optional icon on the left side */
  leftIcon?: ReactNode
  /** Optional icon or action on the right side */
  rightIcon?: ReactNode
  /** Visually hide label while keeping it accessible to screen readers */
  hideLabel?: boolean
  /** Whether the input can be cleared with a clear button */
  isClearable?: boolean
  /** Callback triggered when clear button is clicked */
  onClear?: () => void
  /** Additional container classes */
  containerClassName?: string
}

let inputCounter = 0

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      name,
      type = 'text',
      size = InputSizes.large,
      label,
      caption,
      error,
      isRequired = false,
      isDisabled,
      disabled,
      placeholder,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      leftIcon,
      rightIcon,
      hideLabel = false,
      isClearable = false,
      onClear,
      className,
      containerClassName,
      'aria-describedby': ariaDescribedByProp,
      ...restProps
    },
    ref
  ) => {
    // Generate unique ID for accessibility if not provided
    const fallbackId = useMemo(() => `input-${++inputCounter}`, [])
    const inputId = id || name || fallbackId
    const captionId = `${inputId}-caption`
    const errorId = `${inputId}-error`

    const isEffectiveDisabled = Boolean(disabled || isDisabled)
    const isError = Boolean(error)

    // Internal value tracking to handle filled / typing states
    const [innerValue, setInnerValue] = useState<string>(
      String(value ?? defaultValue ?? '')
    )
    const [isFocused, setIsFocused] = useState(false)
    const [isTyping, setIsTyping] = useState(false)

    // Keep innerValue in sync with controlled value prop
    const currentValue = value !== undefined ? String(value) : innerValue
    const isFilled = currentValue.length > 0

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        if (value === undefined) {
          setInnerValue(e.target.value)
        }
        setIsTyping(true)
        onChange?.(e)
      },
      [value, onChange]
    )

    const handleFocus = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        setIsFocused(true)
        onFocus?.(e)
      },
      [onFocus]
    )

    const handleBlur = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        setIsFocused(false)
        setIsTyping(false)
        onBlur?.(e)
      },
      [onBlur]
    )

    const handleClear = useCallback(() => {
      if (value === undefined) {
        setInnerValue('')
      }
      onClear?.()
    }, [value, onClear])

    // Compute size classes matching button tokens
    const sizeStyles = useMemo(() => {
      switch (size) {
        case InputSizes.small:
          return {
            container: 'h-wcag-min-target min-h-wcag-min-target px-medium text-body-medium',
            input: 'text-body-medium',
            icon: 'w-4 h-4',
            clearBtn: 'p-1',
          }
        case InputSizes.medium:
          return {
            container: 'h-huge min-h-huge px-semi-large text-body-large',
            input: 'text-body-large',
            icon: 'w-4 h-4',
            clearBtn: 'p-1.5',
          }
        case InputSizes.large:
        default:
          return {
            container: 'h-extra-huge min-h-extra-huge px-large text-body-large',
            input: 'text-body-large',
            icon: 'w-5 h-5',
            clearBtn: 'p-2',
          }
      }
    }, [size])

    // Current interaction state for styling & data attribute
    const currentState = isEffectiveDisabled
      ? 'disabled'
      : isError
      ? 'error'
      : isTyping
      ? 'typing'
      : isFocused
      ? 'focused'
      : isFilled
      ? 'filled'
      : 'default'

    // Compute aria-describedby linking error and/or caption
    const computedAriaDescribedBy = useMemo(() => {
      const ids: string[] = []
      if (ariaDescribedByProp) ids.push(ariaDescribedByProp)
      if (isError && typeof error === 'string') ids.push(errorId)
      if (caption) ids.push(captionId)
      return ids.length > 0 ? ids.join(' ') : undefined
    }, [ariaDescribedByProp, isError, error, errorId, caption, captionId])

    return (
      <div className={clsx('w-full flex flex-col gap-1.5 text-left font-palanquin', containerClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={clsx(
              'text-body-small font-semibold select-none pl-4 transition-colors duration-200',
              hideLabel && 'sr-only',
              isEffectiveDisabled
                ? 'text-on-surface-disabled'
                : isError
                ? 'text-on-error-default'
                : 'text-on-surface-variant'
            )}
          >
            {label}
            {isRequired && (
              <span className="text-on-error-default font-bold ml-1" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}

        {/* Input Container */}
        <div
          data-state={currentState}
          className={clsx(
            'group relative w-full flex items-center gap-3 rounded-full border transition-all duration-300 select-none',
            sizeStyles.container,
            // Disabled State
            isEffectiveDisabled &&
              'bg-surface-disabled border-surface-disabled text-on-surface-disabled cursor-not-allowed opacity-60 pointer-events-none',
            // Error State
            !isEffectiveDisabled &&
              isError &&
              'bg-surface-subtle/30 dark:bg-surface-background/80 border-on-error-default text-on-surface focus-within:border-on-error-default focus-within:ring-2 focus-within:ring-on-error-default',
            // Focused State (when not error)
            !isEffectiveDisabled &&
              !isError &&
              isFocused &&
              'bg-surface dark:bg-surface-background border-focus-low ring-2 ring-focus-low',
            // Default / Idle State (when not focused, error or disabled)
            !isEffectiveDisabled &&
              !isError &&
              !isFocused &&
              clsx(
                'bg-surface-subtle/30 dark:bg-surface-background/80 border-outline-lower hover:border-outline-low dark:border-outline-low dark:hover:border-outline-base',
                'focus-within:border-focus-low focus-within:ring-2 focus-within:ring-focus-low',
                isFilled && 'text-on-surface'
              )
          )}
        >
          {/* Left Icon */}
          {leftIcon && (
            <span
              className={clsx(
                'shrink-0 flex items-center justify-center transition-colors duration-200',
                sizeStyles.icon,
                isEffectiveDisabled
                  ? 'text-on-surface-disabled'
                  : isError
                  ? 'text-on-error-default'
                  : isFocused
                  ? 'text-focus-low'
                  : 'text-on-surface-variant-muted'
              )}
              aria-hidden="true"
            >
              {leftIcon}
            </span>
          )}

          {/* Native Input */}
          <input
            ref={ref}
            id={inputId}
            name={name}
            type={type}
            value={value}
            defaultValue={defaultValue}
            placeholder={placeholder}
            disabled={isEffectiveDisabled}
            required={isRequired}
            aria-required={isRequired}
            aria-invalid={isError ? 'true' : 'false'}
            aria-describedby={computedAriaDescribedBy}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={clsx(
              'w-full h-full bg-transparent font-palanquin outline-none border-none p-0 m-0 focus:outline-none focus:ring-0 focus:border-transparent',
              'text-on-surface placeholder:text-on-surface-variant-muted',
              'disabled:cursor-not-allowed disabled:text-on-surface-disabled',
              sizeStyles.input,
              className
            )}
            {...restProps}
          />

          {/* Clear Button */}
          {isClearable && !isEffectiveDisabled && isFilled && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Wyczyść pole"
              className={clsx(
                'shrink-0 rounded-full text-on-surface-variant-muted hover:text-on-surface transition-colors cursor-pointer',
                sizeStyles.clearBtn
              )}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}

          {/* Right Icon */}
          {rightIcon && (
            <span
              className={clsx(
                'shrink-0 flex items-center justify-center transition-colors duration-200',
                sizeStyles.icon,
                isEffectiveDisabled
                  ? 'text-on-surface-disabled'
                  : isError
                  ? 'text-on-error-default'
                  : 'text-on-surface-variant-muted'
              )}
              aria-hidden="true"
            >
              {rightIcon}
            </span>
          )}
        </div>

        {/* Error message or Caption */}
        {isError && typeof error === 'string' ? (
          <p
            id={errorId}
            role="alert"
            aria-live="polite"
            className="text-label-large text-on-error-default font-medium pl-4 select-none"
          >
            {error}
          </p>
        ) : caption ? (
          <p
            id={captionId}
            className={clsx(
              'text-label-large pl-4 select-none',
              isError
                ? 'text-on-error-default font-medium'
                : 'text-on-surface-variant-muted'
            )}
          >
            {caption}
          </p>
        ) : null}
      </div>
    )
  }
)

Input.displayName = 'Input'
